import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { ScrollArea } from '../ui/scroll-area';
import { Loader2, Check, Sparkles } from 'lucide-react';
import { API_URL } from '@/config/api';
import { toast } from '@/components/ui/use-toast';

interface Sector {
  id: string | number; // Can be integer from DB or string from API
  name: string;
  description?: string;
  parent_id?: string | number;
}

interface SectorAssignmentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  siteId: string;
  siteName: string;
  currentSectorIds: (string | number)[];
  onUpdate: (sectorIds: string[]) => void;
}

export function SectorAssignmentModal({
  open,
  onOpenChange,
  siteId,
  siteName,
  currentSectorIds,
  onUpdate,
}: SectorAssignmentModalProps) {
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [selectedSectors, setSelectedSectors] = useState<string[]>(currentSectorIds);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detectingAI, setDetectingAI] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState<{sectorId: string; sectorName: string; confidence: number; reasoning: string} | null>(null);

  useEffect(() => {
    if (open) {
      fetchSectors();
      // Convert integer IDs to strings for consistent handling
      setSelectedSectors(currentSectorIds.map(id => String(id)));
    }
  }, [open, currentSectorIds]);

  const fetchSectors = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/sectors`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setSectors(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching sectors:', error);
      toast({
        title: 'Error',
        description: 'Failed to load sectors',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSector = (sectorId: string) => {
    setSelectedSectors(prev => 
      prev.includes(sectorId) 
        ? prev.filter(id => id !== sectorId)
        : [...prev, sectorId]
    );
  };

  const handleAISuggestion = async () => {
    setDetectingAI(true);
    setAiSuggestion(null);
    
    try {
      const response = await fetch(`${API_URL}/sites/${siteId}/detect-sector`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setAiSuggestion(result.data);
          
          // Auto-select the suggested sector
          if (result.data.sectorId && !selectedSectors.includes(String(result.data.sectorId))) {
            setSelectedSectors(prev => [...prev, String(result.data.sectorId)]);
          }
          
          toast({
            title: 'AI Suggestion Ready',
            description: `Suggested: ${result.data.sectorName} (${Math.round(result.data.confidence * 100)}% confidence)`,
          });
        }
      } else {
        throw new Error('Failed to get AI suggestion');
      }
    } catch (error) {
      console.error('Error getting AI suggestion:', error);
      toast({
        title: 'Error',
        description: 'Failed to get AI sector suggestion. Please select manually.',
        variant: 'destructive',
      });
    } finally {
      setDetectingAI(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Convert sector IDs from strings to integers
      const sectorIdsAsIntegers = selectedSectors.map(id => parseInt(id, 10));

      const response = await fetch(`${API_URL}/sites/${siteId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify({ sector_ids: sectorIdsAsIntegers }),
      });
      
      if (response.ok) {
        onUpdate(selectedSectors);
        onOpenChange(false);
        toast({
          title: 'Success',
          description: 'Site sectors updated successfully',
        });
      } else {
        throw new Error('Failed to update sectors');
      }
    } catch (error) {
      console.error('Failed to update sectors:', error);
      toast({
        title: 'Error',
        description: 'Failed to update site sectors',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // Group sectors by parent
  const groupedSectors = sectors.reduce((acc, sector) => {
    const parentId = sector.parent_id || 'root';
    if (!acc[parentId]) {
      acc[parentId] = [];
    }
    acc[parentId].push(sector);
    return acc;
  }, {} as Record<string, Sector[]>);

  // Get parent sectors
  const parentSectors = sectors.filter(s => !s.parent_id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Assign Sectors</DialogTitle>
          <DialogDescription>
            Select the sectors that apply to <strong>{siteName}</strong>
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Currently assigned:</span>
                  {currentSectorIds.length === 0 ? (
                    <span className="text-sm text-muted-foreground">None</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {currentSectorIds.map(id => {
                        // Handle both integer and string IDs
                        const sector = sectors.find(s => String(s.id) === String(id));
                        return sector ? (
                          <Badge key={String(id)} variant="secondary" className="text-xs">
                            {sector.name}
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAISuggestion}
                  disabled={detectingAI}
                >
                  {detectingAI ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Get AI Suggestion
                    </>
                  )}
                </Button>
              </div>
              
              {aiSuggestion && (
                <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-start gap-2">
                    <Sparkles className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                        AI Suggestion: {aiSuggestion.sectorName}
                      </p>
                      <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                        Confidence: {Math.round(aiSuggestion.confidence * 100)}%
                      </p>
                      <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                        {aiSuggestion.reasoning}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-4">
                {parentSectors.map(parent => (
                  <div key={parent.id} className="space-y-2">
                    <div className="flex items-center space-x-2 py-2">
                      <Checkbox
                        id={String(parent.id)}
                        checked={selectedSectors.includes(String(parent.id))}
                        onCheckedChange={() => handleToggleSector(String(parent.id))}
                      />
                      <label
                        htmlFor={String(parent.id)}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex-1"
                      >
                        {parent.name}
                        {parent.description && (
                          <span className="text-xs text-muted-foreground block mt-1">
                            {parent.description}
                          </span>
                        )}
                      </label>
                    </div>
                    
                    {groupedSectors[parent.id] && groupedSectors[parent.id].length > 0 && (
                      <div className="ml-6 space-y-2">
                        {groupedSectors[parent.id].map(child => (
                          <div key={child.id} className="flex items-center space-x-2 py-1">
                            <Checkbox
                              id={String(child.id)}
                              checked={selectedSectors.includes(String(child.id))}
                              onCheckedChange={() => handleToggleSector(String(child.id))}
                            />
                            <label
                              htmlFor={String(child.id)}
                              className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex-1"
                            >
                              {child.name}
                              {child.description && (
                                <span className="text-xs text-muted-foreground block mt-1">
                                  {child.description}
                                </span>
                              )}
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                
{/* NOTE: Root level sectors are already shown in parentSectors loop above */}
              </div>
            </ScrollArea>

            <div className="text-sm text-muted-foreground">
              {selectedSectors.length} sector{selectedSectors.length !== 1 ? 's' : ''} selected
            </div>
          </>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="mr-2 h-4 w-4" />
                Save Changes
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}